import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiPostClinics } from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { PhysiciansModal } from "../common/physicians-modal";

export const AddClinicDialog = observer(function AddClinicDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminConsole;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const [fields] = useState(() =>
    observable({
      name: "",
      ambulance: null as string | null,
      address: "",
      country: "",
      region: null as number | null,
      phone: null as string | null,
      email: null as string | null,
      physicians_uuids: [] as string[],
    }),
  );

  async function save() {
    await apiPostClinics({
      body: {
        ...fields,
        region: fields.region ?? undefined,
      },
    });
    admin.api.clearResponseCache();
    onSaved();
    onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add new clinic</Trans>
      </h3>
      <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
        <div className="overflow-y-auto min-h-0 px-1 py-1">
          <FormTable dataAdd>
            <FormRow
              label={t`Name`}
              data={<TextInput attr="name" model={fields} required />}
              mandatory
            />
            <FormRow
              label={t`Ambulance`}
              data={<TextInput attr="ambulance" model={fields} />}
            />
            <FormRow
              label={t`Address`}
              data={<TextInput attr="address" model={fields} required />}
              mandatory
            />
            <FormRow
              label={t`Country`}
              data={
                <CountrySelect
                  attr="country"
                  model={fields}
                  regionAttr="region"
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Region`}
              data={
                <RegionSelect
                  attr="region"
                  model={fields}
                  country={fields.country || undefined}
                  required
                />
              }
            />
            <FormRow
              label={t`(General) Phone`}
              data={<TextInput attr="phone" model={fields} />}
            />
            <FormRow
              label={t`(General) Email`}
              data={<TextInput attr="email" model={fields} />}
            />
            <FormRow
              label={t`Clinic physicians`}
              data={
                <ChooseField
                  count={fields.physicians_uuids.length}
                  modal={({ onClose }) => {
                    const physicians = admin.createPhysicians();
                    return (
                      <PhysiciansModal
                        title={t`Choose physicians for a new clinic`}
                        preselected={fields.physicians_uuids}
                        filter={{ country: [fields.country] }}
                        mode="multi"
                        model={physicians}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.physicians_uuids = selected;
                        }}
                      />
                    );
                  }}
                />
              }
            />
          </FormTable>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit">
            <Trans>Add clinic</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});
